import { useState } from 'react';

import { Badge, Divider, ErrorFillIcon, Label, LinkOpenIcon, Modal, Text, TextButton } from '@thefarmersfront/kitchen';

import {
  DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR,
  DELIVERY_AGENCY_TRACKING_LINKS,
} from '~/constants';

import type { DeliveryResultReportDetail } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyReportDetailModal.module.scss';

/**
 * [배송 결과 리포트] 배송 결과 상세 모달 컴포넌트
 */
const DeliveryAgencyReportDetailModal = ({
  open,
  data,
  onClose,
}: {
  open: boolean;
  data: DeliveryResultReportDetail;
  onClose: () => void;
}) => {
  const [isImageError, setIsImageError] = useState(false);

  const trackingUrl =
    data.courierCode.code in DELIVERY_AGENCY_TRACKING_LINKS && data.shippingLabelNumber
      ? DELIVERY_AGENCY_TRACKING_LINKS[data.courierCode.code as keyof typeof DELIVERY_AGENCY_TRACKING_LINKS] +
        data.shippingLabelNumber
      : undefined;

  const showImage = !!data.deliveryCompletedImageUrl && !isImageError;

  return (
    <Modal title="배송 결과 상세" size="small" open={open} onConfirm={onClose}>
      <div className={styles['container']}>
        {showImage ? (
          <img
            className={styles['delivery-completed-image']}
            src={data.deliveryCompletedImageUrl ?? ''}
            alt="배송완료 사진"
            onError={() => setIsImageError(true)}
          />
        ) : (
          <div className={styles['delivery-completed-image-placeholder']}>
            <ErrorFillIcon size={64} color="#bcc4cc" />
            <div className={styles['placeholder-text']}>
              <Text size="xxlarge" weight="semi-bold">
                배송 완료 사진을 불러올 수 없어요
              </Text>
              <Text size="xlarge" color="tertiary">
                관리자에게 문의해주세요.
              </Text>
            </div>
          </div>
        )}
        <div className={styles['info']}>
          <div className={styles['info-header']}>
            <Badge
              size="medium"
              variant="light"
              status
              color={DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR[data.reportedShippingLabelStatus.code]}
            >
              {data.reportedShippingLabelStatus.description}
            </Badge>
            <div className={styles['shipping-label-row']}>
              <Text size="xlarge" weight="semi-bold">
                {data.shippingLabelNumber}
              </Text>
              <TextButton
                size="tiny"
                weight="semi-bold"
                variant="normal"
                target="_blank"
                href={trackingUrl}
                rightIcon={<LinkOpenIcon size={16} color="#672091" />}
              >
                배송 조회
              </TextButton>
            </div>
          </div>
          <div className={styles['info-detail']}>
            <div className={styles['row']}>
              <Label title="배송결과" />
              {data.reportedDeliveryResults.length > 0 ? (
                <div className={styles['badge-wrap']}>
                  {data.reportedDeliveryResults.map((result) => (
                    <Badge
                      key={result.code}
                      size="small"
                      variant="light"
                      color={DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR[result.code]}
                    >
                      {result.description}
                    </Badge>
                  ))}
                </div>
              ) : (
                <Text size="large">-</Text>
              )}
            </div>
            <Divider color="gray100" />
            <div className={styles['row']}>
              <Label title="처리 장소" />
              <Text size="large">{data.actualDeliveryLocationType || '-'}</Text>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeliveryAgencyReportDetailModal;
